// @ts-check
import { gql, useMutation, useQuery } from '@apollo/client';
import { Trans } from '@lingui/macro';
import { Form, Formik } from 'formik';
import React from 'react';
import { Box, Text } from '~/design-system';
import ImportOptionsSkeleton from '../ImportOptionsSkeleton';
import ReadyCheckboxField from '../ReadyCheckboxField';

/**
 * Renders the ready to import page with the import statistics
 * The view. The props are passed in from useReady - all the logic resides there.
 * @param {{
 *    setFormikContext: ReturnType<import('../../useFormikOrchestration').default>["setFormikContext"]
 * } & ReturnType<useReady>} props
 * @returns
 */
export default function ReadyToImport({
  importData,
  loading,
  setFormikContext,
  onSubmit,
}) {
  if (loading)
    return (
      <Box>
        <ImportOptionsSkeleton />
      </Box>
    );
  const { preview: previewStatistics } = importData || {};
  return (
    <Box>
      <ImportOptions
        statistics={previewStatistics}
        setFormikContext={setFormikContext}
        onSubmit={onSubmit}
      />
    </Box>
  );
}

function ImportOptions({ statistics, setFormikContext, onSubmit }) {
  const productRequestStatistics = statistics.find(
    (stat) => stat.object === 'PRODUCT_REQUEST'
  );
  const initialValues = {
    added: productRequestStatistics?.addedCount > 0,
    updated: productRequestStatistics?.updatedCount > 0,
    unchanged: false,
  };
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(props) => {
        // Map to import-specific flags
        const importOptions = {
          updateRequests: props.updated,
        };
        onSubmit(importOptions);
      }}
    >
      {(formikContext) => {
        setFormikContext(formikContext);
        return (
          <Form>
            <Box>
              <Box sx={{ textAlign: 'center' }}>
                <Text sx={{ marginTop: 3 }}>
                  <Trans>All your data is valid and ready to import.</Trans>
                </Text>
              </Box>
              <Box sx={{ margin: 'auto', width: '330px', textAlign: 'left' }}>
                <ReadyCheckboxField
                  name="unchanged"
                  disabled
                  count={productRequestStatistics?.unchangedCount}
                >
                  <Trans>Requests have no change</Trans>
                </ReadyCheckboxField>
                <ReadyCheckboxField
                  name="updated"
                  count={productRequestStatistics?.updatedCount}
                  disabled={!initialValues.updated}
                >
                  <Trans>Requests that will be updated</Trans>
                </ReadyCheckboxField>
              </Box>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
}

/**
 * @param {object} props
 * @param {string} props.workspaceId
 * @param {string} props.importId
 */
export function useReady({ workspaceId, importId }) {
  const { data, error, loading } = useQuery(QUERY, {
    variables: { workspaceId, importId },
  });
  const [importExternalIds] = useMutation(IMPORT_EXTERNAL_IDS);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  return {
    importId,
    ...afterQuery(data),
    error,
    loading,
    isSubmitting,
    onSubmit: async (importOptions) => {
      setIsSubmitting(true);
      const variables = {
        importId,
        importOptions,
      };
      await importExternalIds({ variables });
      setIsSubmitting(false);
    },
  };
}

/** @param {import('./ReadyToImport.generated').ExternalIdReadyPageQuery | undefined} data */
function afterQuery(data) {
  if (!data) return;
  return {
    importData: data?.workspace?.dataImport,
    stats: data?.workspace?.dataImport?.preview,
  };
}

/** @type {typeof import('./ReadyToImport.generated').ExternalIdReadyPageDocument} */
const QUERY = gql`
  query ExternalIdReadyPage($workspaceId: ID!, $importId: ID!) {
    workspace(workspaceId: $workspaceId) {
      workspaceId
      dataImport(importId: $importId) {
        importId
        importOptions
        preview {
          deletedCount
          object
          updatedCount
          unchangedCount
          addedCount
        }
      }
    }
  }
`;

/** @type {typeof import('./ReadyToImport.generated').FinalizeProductExternalIdImportDocument} */
export const IMPORT_EXTERNAL_IDS = gql`
  mutation finalizeProductExternalIdImport(
    $importId: ID!
    $importOptions: ProductExternalIdImportOptionsInput!
  ) {
    result: finalizeProductExternalIdImport(
      input: { importId: $importId, importOptions: $importOptions }
    ) {
      code
      success
      message
    }
  }
`;
